import { useState } from "react";
import { toast } from "react-toastify";
import { EQAuthParamType } from "src/Types";
import { EQueueConfigType, EQueueType } from "src/Types/ReDoc/EQueue";
import { DamaskRedirectParamType } from "src/Types/ReDoc/Damask";
import { toStringPayload, toRedirectPayload, hasActionError, getActionErrorMessage, returnToastStyle } from "../eQueueUtils";

const formatQueueCount = (n: number) => {
	if (n % 10 === 1 && n % 100 !== 11) return `${n} посетитель`;
	if ([2, 3, 4].includes(n % 10) && ![12, 13, 14].includes(n % 100)) return `${n} посетителя`;
	return `${n} посетителей`;
};

type ReturnTransferConfirmation = {
	reason: string;
	comment: string;
};

const buildTransferComment = ({ reason, comment }: ReturnTransferConfirmation): string => {
	const reasonText = `Причина перенаправления: ${reason}`;
	return comment ? `${reasonText}. Комментарий: ${comment}` : reasonText;
};

interface UseEQActionsParams {
	type: EQueueType;
	isReturnQueue: boolean;
	idQ: string;
	config?: EQueueConfigType;
	queueCount: number;
	showError: (message: string, title: string) => void;
	setShowWidget: (v: boolean) => void;
	dispatch: (action: string, payload?: unknown) => void;
	saveConfig: (payload: EQAuthParamType) => unknown;
	getStatus?: () => unknown;
	getQuery: (term?: string) => unknown;
	call: () => unknown;
	previewNext: () => unknown;
	confirmCall: () => unknown;
	callById: (payload: string) => unknown;
	beginPause: (payload: { reason: string; idQ: string }) => unknown;
	beginBreakFromCalling: (reason: string) => unknown;
	replaceSelected: (ticketId: string) => unknown;
	endPause: () => unknown;
	drop: (payload: string) => unknown;
	recall: () => unknown;
	cancelCall: (reason: string) => unknown;
	beginServing: (payload?: string) => unknown;
	delay: (payload: string) => unknown;
	redirect: (payload: DamaskRedirectParamType) => unknown;
	redirectWindow: (payload: { toWindow: string; comment: string }) => unknown;
	getDenyReasons: () => unknown;
	reason: (payload: string) => unknown;
	endServing: (payload: string) => unknown;
	exit: (payload: string) => unknown;
	start: (payload: string) => unknown;
	getWindows: (payload?: { transfer?: boolean; officeId?: number }) => unknown;
}

export const useEQActions = (params: UseEQActionsParams) => {
	const {
		type, isReturnQueue, idQ, config, queueCount, showError, setShowWidget,
		dispatch, saveConfig, getStatus, getQuery, call, previewNext, confirmCall, callById,
		beginPause, beginBreakFromCalling, replaceSelected, endPause, drop,
		recall, cancelCall, beginServing, delay, redirect, redirectWindow,
		getDenyReasons, reason, endServing, exit, start, getWindows
	} = params;

	const [ postCompleteAction, setPostCompleteAction ] = useState<string | null>(null);
	const [ transferWindow, setTransferWindow ] = useState<string>("");
	const [ isReplacingSelected, setIsReplacingSelected ] = useState<boolean>(false);

	const onCloseModal = () => {
		setIsReplacingSelected(false);
		dispatch("closeModal");
	};

	const openPostCompleteModal = (action: string) => {
		switch (action) {
			case "endServiceAndInviteByNumber":
				if (type === EQueueType.Enter) getQuery();
				dispatch("inviteByNumberModal");
				break;
			case "endServiceAndCap":
				dispatch("askCapModal");
				break;
			case "endServiceAndExit":
				dispatch("askExitModal");
				break;
		}
	};

	const runPostCompleteAction = () => {
		onCloseModal();
		if (postCompleteAction) {
			openPostCompleteModal(postCompleteAction);
			setPostCompleteAction(null);
		}
	};

	const handleEndServiceWithPostAction = (action: string) => {
		if (type === EQueueType.Enter) {
			getDenyReasons();
			setPostCompleteAction(action);
			dispatch("askEndServiceModal");
		} else {
			openPostCompleteModal(action);
		}
	};

	const openInviteByNumberModal = (shouldLoadQueue: boolean = false, searchTerm: string = "") => {
		if (shouldLoadQueue) {
			if (isReturnQueue) getQuery(searchTerm);
			if (type === EQueueType.Enter) getQuery();
		}
		dispatch("inviteByNumberModal");
	};

	const setReturnFreeState = () => {
		dispatch("setViewMode", "free");
		dispatch("clearMockTicket");
	};

	const openReturnWindow = async (windowId: string) => {
		if (!windowId) {
			showError("Не удалось определить окно для запуска очереди", "Открытие окна");
			return;
		}
		const nextConfig: EQAuthParamType = {
			type,
			login: config?.login ?? "",
			password: config?.password ?? "",
			pultId: windowId
		};
		try {
			const saveResult = await Promise.resolve(saveConfig(nextConfig));
			if (hasActionError(saveResult)) {
				showError(getActionErrorMessage(saveResult) || "Не удалось сохранить выбранное окно", "Открытие окна");
				return;
			}
			const startResult = await Promise.resolve(start(windowId));
			if (hasActionError(startResult)) {
				showError(getActionErrorMessage(startResult) || "Не удалось открыть окно", "Открытие окна");
				return;
			}
			if (getStatus) {
				getStatus();
			}
		}
		catch (error) {
			const message = error instanceof Error ? error.message : "Не удалось открыть окно";
			showError(message, "Открытие окна");
		}
	};

	const onActionHandler = (actionName: string, payload?: unknown) => {
		if (window.isDev) console.log("Action - ", actionName, payload);

		switch (actionName) {
			case "asc-inviteByNumber":
				openInviteByNumberModal(type === EQueueType.Enter);
				break;
			case "cap":
				dispatch("askCapModal");
				break;
			case "cancelCap":
				dispatch("cancelCapModal");
				break;
			case "asc-notAppear":
				dispatch("askCancelCall");
				break;
			case "asc-delay":
				dispatch("ascDelay");
				break;
			case "asc-redirect":
				dispatch("ascRedirect");
				break;
			case "asc-exit":
				dispatch("askExitModal");
				break;
			case "invite":
				call();
				break;
		case "inviteByNumber": {
			if (isReturnQueue) {
				const ticketId = toStringPayload(payload);
				if (!ticketId) { showError("Не удалось определить талон для вызова", "Вызов по талону"); break; }
				if (isReplacingSelected) {
					setIsReplacingSelected(false);
					replaceSelected(ticketId);
				} else {
					callById(ticketId);
				}
				break;
			}
			callById(toStringPayload(payload));
			break;
		}
			case "beginPause":
				beginPause({ reason: toStringPayload(payload), idQ });
				onCloseModal();
				break;
			case "endPause":
				if (isReturnQueue) {
					dispatch("askReturnEndPauseModal");
					break;
				}
				endPause();
				break;
			case "return-confirm-end-pause":
				endPause();
				onCloseModal();
				break;
			case "notAppear":
				drop(idQ || "");
				break;
			case "repeatCall":
				recall();
				break;
			case "beginServing":
				beginServing(idQ || undefined);
				break;
			case "delay":
				delay(toStringPayload(payload));
				onCloseModal();
				break;
			case "redirect": {
				const redirectPayload = toRedirectPayload(payload);
				if (!!redirectPayload) redirect(redirectPayload);
				onCloseModal();
				break;
			}
			case "endService":
				if (type === EQueueType.Enter) {
					getDenyReasons();
					dispatch("askEndServiceModal");
				} else if (isReturnQueue) {
					dispatch("askEndServiceModal");
				} else {
					endServing(idQ || "");
				}
				break;
			case "complete":
				endServing(idQ || "");
				runPostCompleteAction();
				break;
			case "reasons":
				reason(toStringPayload(payload));
				runPostCompleteAction();
				break;
			case "endServiceAndInviteByNumber":
			case "endServiceAndCap":
			case "endServiceAndExit":
				handleEndServiceWithPostAction(actionName);
				break;
			case "exit":
				exit(idQ);
				setShowWidget(false);
				break;
			case "return-open-window":
				getWindows({ transfer: false, officeId: config?.officeId });
				break;
			case "return-open-window-select":
				openReturnWindow(toStringPayload(payload));
				break;
			case "return-invite":
				dispatch("setViewMode", "confirming");
				break;
			case "return-invite-queue":
				previewNext();
				break;
			case "return-invite-ticket":
				if (config?.callByTicketNumberEnabled === false) {
					break;
				}
				openInviteByNumberModal(true);
				break;
		case "return-invite-another":
			if (config?.callAnotherEnabled === false) {
				break;
			}
			setIsReplacingSelected(true);
			openInviteByNumberModal(true);
			break;
			case "return-search-ticket":
				getQuery(toStringPayload(payload));
				break;
			case "return-confirm":
				confirmCall();
				break;
			case "return-repeat-call":
				recall();
				break;
			case "return-stop-call":
				dispatch("askReturnCancelCallModal");
				break;
			case "return-stop-call-confirm":
				onCloseModal();
				cancelCall(toStringPayload(payload));
				break;
			case "return-stop-call-notappear":
				drop("");
				break;
		case "return-stop-call-rest":
			dispatch("askReturnBreakFromCallingModal");
			break;
		case "return-break-from-calling-confirm":
			onCloseModal();
			beginBreakFromCalling(toStringPayload(payload) || "");
			break;
			case "return-stop-call-finish":
				dispatch("askReturnWorkdayEndModal");
				break;
			case "return-redirect":
				if (config?.transferEnabled === false) {
					break;
				}
				getWindows({ transfer: true, officeId: config?.officeId });
				break;
			case "return-redirect-select": {
				if (config?.transferEnabled === false) {
					break;
				}
				const toWindow = toStringPayload(payload);
				if (!toWindow) { showError("Не удалось определить окно для перенаправления", "Перенаправление"); break; }
				setTransferWindow(toWindow);
				dispatch("askReturnTransferModal");
				break;
			}
			case "return-redirect-confirm": {
				const transfer = payload as ReturnTransferConfirmation | undefined;
				if (!transferWindow || !transfer?.reason?.trim()) break;
				onCloseModal();
				const comment = buildTransferComment(transfer);
				Promise.resolve(redirectWindow({ toWindow: transferWindow, comment })).then((res: unknown) => {
					if (hasActionError(res)) {
						showError(getActionErrorMessage(res) || "Не удалось перенаправить посетителя", "Перенаправление");
					}
				});
				setTransferWindow("");
				break;
			}
			case "goRest":
				dispatch("askReturnPauseModal");
				break;
			case "finishWork":
				dispatch("askReturnWorkdayEndModal");
				break;
			case "return-finish-work":
				onCloseModal();
				if (queueCount > 0) {
					toast.error(
						`В очереди ещё ожидают ${formatQueueCount(queueCount)} — их талоны останутся активными`,
						{
							position: "top-center",
							autoClose: 10000,
							hideProgressBar: true,
							closeOnClick: true,
							pauseOnHover: true,
							draggable: true,
							closeButton: false,
							style: returnToastStyle
						}
					);
				}
				exit(idQ);
				setShowWidget(false);
				break;
		}
	};

	return { onActionHandler, onCloseModal, postCompleteAction, setPostCompleteAction };
};
